/**
 * 移动端订单详情页
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  List,
  Skeleton,
  Button,
  Dialog,
  Form,
  Input,
  TextArea,
  showToast,
  Space,
  Divider,
  Tag,
  Dot,
} from 'antd-mobile';
import { adminOrderApi } from '../../api/index';
import Toast from 'antd-mobile/es/components/toast';

export default function MobileOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [refunds, setRefunds] = useState([]);
  const [shipModalVisible, setShipModalVisible] = useState(false);
  const [refundModalVisible, setRefundModalVisible] = useState(false);
  const [noteModalVisible, setNoteModalVisible] = useState(false);
  const [shipForm] = Form.useForm();
  const [refundForm] = Form.useForm();
  const [noteForm] = Form.useForm();
  const [noteSaving, setNoteSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // 编辑收货地址
  const [editAddressModalVisible, setEditAddressModalVisible] = useState(false);
  const [addressForm] = Form.useForm();
  const [addressSaving, setAddressSaving] = useState(false);
  // 退款 OTP
  const [otpSending, setOtpSending] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);

  useEffect(() => {
    if (otpCooldown <= 0) return;
    const t = setTimeout(() => setOtpCooldown(otpCooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [otpCooldown]);

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const fetchOrder = async () => {
    try {
      const data = await adminOrderApi.getById(id);
      setOrder(data);
      noteForm.setFieldsValue({ adminNote: data.adminNote || '' });
    } catch (err) {
      Toast.show('订单不存在');
      navigate('/admin/m/orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchRefunds = async () => {
    try {
      const data = await adminOrderApi.getRefunds(id);
      setRefunds(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('获取退款记录失败', err);
    }
  };

  useEffect(() => {
    if (order?.id) {
      fetchRefunds();
    }
  }, [order?.id]);

  const handleShip = async (values) => {
    try {
      await adminOrderApi.ship(id, {
        trackingNumber: values.trackingNumber,
        trackingCarrier: values.trackingCarrier || 'SF',
      });
      Toast.show('发货成功');
      setShipModalVisible(false);
      shipForm.resetFields();
      fetchOrder();
    } catch (err) {
      Toast.show(err?.response?.data?.message || '发货失败');
    }
  };

  const handleUpdateStatus = async (status) => {
    try {
      await adminOrderApi.updateStatus(id, status);
      Toast.show('状态更新成功');
      fetchOrder();
    } catch (err) {
      Toast.show(err?.response?.data?.message || '更新失败');
    }
  };

  const confirmCancel = () => {
    Dialog.confirm({
      content: '确定要取消此订单吗？',
      onConfirm: async () => {
        setCancelling(true);
        try {
          await adminOrderApi.cancel(id);
          Toast.show('订单已取消');
          fetchOrder();
        } catch (err) {
          Toast.show(err?.response?.data?.message || '取消失败');
        } finally {
          setCancelling(false);
        }
      },
    });
  };

  const confirmDelete = () => {
    Dialog.confirm({
      content: '确定要删除此订单吗？删除后无法恢复。',
      onConfirm: async () => {
        setDeleting(true);
        try {
          await adminOrderApi.delete(id);
          Toast.show('订单已删除');
          navigate('/admin/m/orders');
        } catch (err) {
          Toast.show(err?.response?.data?.message || '删除失败');
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  const handleSendRefundOtp = async () => {
    setOtpSending(true);
    try {
      await adminOrderApi.sendRefundOtp(order.id);
      Toast.show('验证码已发送至管理员邮箱');
      setOtpCooldown(60);
    } catch (err) {
      Toast.show(err?.response?.data?.message || '发送验证码失败');
    } finally {
      setOtpSending(false);
    }
  };

  const handleRefund = async (values) => {
    try {
      const payments = order.payments || [];
      if (payments.length === 0) {
        Toast.show('没有支付记录');
        return;
      }
      if (!values.otp || String(values.otp).length !== 6) {
        Toast.show('请输入 6 位验证码');
        return;
      }
      const payment = payments[0];
      await adminOrderApi.refund(order.id, payment.id, Number(values.amount), values.reason, values.otp);
      Toast.show('退款成功');
      setRefundModalVisible(false);
      setOtpCooldown(0);
      refundForm.resetFields();
      fetchOrder();
      fetchRefunds();
    } catch (err) {
      Toast.show(err?.response?.data?.message || '退款失败');
    }
  };

  const handleSaveNote = async () => {
    const values = await noteForm.validateFields();
    setNoteSaving(true);
    try {
      await adminOrderApi.update(id, { adminNote: values.adminNote });
      Toast.show('备注已保存');
      setNoteModalVisible(false);
      fetchOrder();
    } catch (err) {
      Toast.show(err?.response?.data?.message || '保存失败');
    } finally {
      setNoteSaving(false);
    }
  };

  // 打开编辑收货地址弹窗
  const handleEditAddressOpen = () => {
    addressForm.setFieldsValue({
      shippingFirstName: order?.shippingFirstName || '',
      shippingLastName: order?.shippingLastName || '',
      shippingPhone: order?.shippingPhone || '',
      shippingAddress1: order?.shippingAddress1 || '',
      shippingAddress2: order?.shippingAddress2 || '',
      shippingCity: order?.shippingCity || '',
      shippingProvince: order?.shippingProvince || '',
      shippingPostalCode: order?.shippingPostalCode || '',
      shippingCountry: order?.shippingCountry || '',
    });
    setEditAddressModalVisible(true);
  };

  // 确认编辑收货地址
  const handleEditAddressConfirm = async () => {
    try {
      const values = await addressForm.validateFields();
      setAddressSaving(true);
      await adminOrderApi.update(order.id, {
        shippingFirstName: values.shippingFirstName,
        shippingLastName: values.shippingLastName,
        shippingPhone: values.shippingPhone,
        shippingAddress1: values.shippingAddress1,
        shippingAddress2: values.shippingAddress2 || '',
        shippingCity: values.shippingCity,
        shippingProvince: values.shippingProvince || '',
        shippingPostalCode: values.shippingPostalCode,
        shippingCountry: values.shippingCountry || '',
      });
      Toast.show('收货地址已更新');
      setEditAddressModalVisible(false);
      fetchOrder();
    } catch (err) {
      if (err.errorFields) return;
      Toast.show(err?.response?.data?.message || '更新失败');
    } finally {
      setAddressSaving(false);
    }
  };

  // 财务状态标签
  const financialStatusLabel = {
    pending: { text: '待支付', color: '#faad14' },
    paid: { text: '已支付', color: '#52c41a' },
    refunded: { text: '已退款', color: '#ff9845' },
    partial_refunded: { text: '部分退款', color: '#ff7a45' },
    voided: { text: '已作废', color: '#999' },
  };

  // 支付状态标签
  const paymentStatusLabel = {
    pending: { text: '待支付', color: '#faad14' },
    success: { text: '支付成功', color: '#52c41a' },
    failure: { text: '支付失败', color: '#ff4d4f' },
    closed: { text: '已关闭', color: '#999' },
  };

  if (loading) {
    return (
      <div style={{ padding: 16 }}>
        <Skeleton.Title rows={3} />
        <Skeleton.Title rows={5} style={{ marginTop: 16 }} />
      </div>
    );
  }

  if (!order) return null;

  const payments = order.payments || [];

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', padding: 16 }}>
      {/* 订单状态 */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 16, fontWeight: 600 }}>{order.orderNo}</span>
          <StatusTag status={order.status} />
        </div>
        <div style={{ fontSize: 14, color: '#666', marginBottom: 8 }}>
          下单时间：{order.createdAt?.slice(0, 16)?.replace('T', ' ')}
        </div>
        <div style={{ fontSize: 14, color: '#666' }}>
          订单金额：<span style={{ fontSize: 18, fontWeight: 600, color: '#f5222d' }}>${order.total?.toFixed(2)}</span>
        </div>
        {order.financialStatus && (
          <div style={{ marginTop: 8 }}>
            <Tag color={financialStatusLabel[order.financialStatus]?.color || 'default'}>
              {financialStatusLabel[order.financialStatus]?.text || order.financialStatus}
            </Tag>
          </div>
        )}
      </Card>

      {/* 操作按钮 */}
      <Card style={{ marginBottom: 16 }}>
        <Space block wrap>
          {order.status === 'pending' && (
            <>
              <Button color="primary" onClick={() => handleUpdateStatus('paid')}>
                标记为已支付
              </Button>
              <Button color="danger" variant="outline" onClick={confirmCancel}>
                取消订单
              </Button>
            </>
          )}
          {order.status === 'paid' && (
            <Button color="primary" onClick={() => setShipModalVisible(true)}>
              发货
            </Button>
          )}
          {order.status === 'shipped' && (
            <Button color="success" onClick={() => handleUpdateStatus('completed')}>
              完成订单
            </Button>
          )}
          {order.status !== 'cancelled' && order.status !== 'completed' && (
            <Button variant="outline" onClick={() => setNoteModalVisible(true)}>
              备注
            </Button>
          )}
          <Button variant="outline" color="danger" onClick={confirmDelete}>
            删除
          </Button>
        </Space>
      </Card>

      {/* 客户信息 */}
      {order.email && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>客户信息</div>
          <div style={{ fontSize: 14 }}>
            邮箱：
            {order.userId ? (
              <a
                onClick={() => navigate(`/admin/m/customers/${order.userId}`)}
                style={{ color: '#1677ff', marginLeft: 4 }}
              >
                {order.email}
              </a>
            ) : (
              <span style={{ color: '#333', marginLeft: 4 }}>{order.email}</span>
            )}
          </div>
        </Card>
      )}

      {/* 收货地址 */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 15, fontWeight: 600 }}>收货地址</span>
          <Button size="small" variant="outline" onClick={handleEditAddressOpen}>
            编辑
          </Button>
        </div>
        <div style={{ fontSize: 14, color: '#333' }}>
          {order.shippingLastName} {order.shippingFirstName}
        </div>
        <div style={{ fontSize: 14, color: '#666', marginTop: 4 }}>
          {order.shippingPhone}
        </div>
        <div style={{ fontSize: 14, color: '#666', marginTop: 4 }}>
          {order.shippingAddress1} {order.shippingAddress2}
        </div>
        <div style={{ fontSize: 14, color: '#666', marginTop: 4 }}>
          {order.shippingCity}, {order.shippingProvince} {order.shippingPostalCode}
        </div>
        <div style={{ fontSize: 14, color: '#666', marginTop: 4 }}>
          {order.shippingCountry}
        </div>
      </Card>

      {/* 物流信息 */}
      {order.trackingNumber && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>物流信息</div>
          <div style={{ fontSize: 14, color: '#666', marginBottom: 4 }}>
            物流公司：{order.trackingCarrier}
          </div>
          <div style={{ fontSize: 14, color: '#333' }}>
            物流单号：{order.trackingNumber}
          </div>
        </Card>
      )}

      {/* 商品列表 */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>商品明细</div>
        <List>
          {order.items?.map((item) => (
            <List.Item
              key={item.id}
              description={
                <div style={{ fontSize: 13, color: '#999' }}>
                  {item.variantTitle || '默认款式'} × {item.quantity}
                </div>
              }
              extra={
                <span style={{ fontSize: 14, fontWeight: 500 }}>
                  ${(item.price * item.quantity).toFixed(2)}
                </span>
              }
            >
              <div style={{ fontSize: 14 }}>{item.title || item.productTitle}</div>
            </List.Item>
          ))}
        </List>
      </Card>

      {/* 价格明细 */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>价格明细</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 8 }}>
          <span style={{ color: '#666' }}>商品小计</span>
          <span>${order.subtotal?.toFixed(2)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 8 }}>
          <span style={{ color: '#666' }}>运费</span>
          <span>${order.shippingFee?.toFixed(2)}</span>
        </div>
        {order.discountAmount > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 8 }}>
            <span style={{ color: '#666' }}>优惠</span>
            <span style={{ color: '#52c41a' }}>-${order.discountAmount?.toFixed(2)}</span>
          </div>
        )}
        <Divider style={{ margin: '8px 0' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 600 }}>
          <span>合计</span>
          <span style={{ color: '#f5222d' }}>${order.total?.toFixed(2)}</span>
        </div>
      </Card>

      {/* 支付记录 */}
      {payments.length > 0 && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>支付记录</div>
          {payments.map((payment, i) => {
            const paymentStatus = paymentStatusLabel[payment.status] || { text: payment.status, color: '#999' };
            const paymentRefunds = refunds.filter(r => r.paymentId === payment.id);
            const alreadyRefunded = paymentRefunds.reduce((sum, r) => sum + (r.amount || 0), 0);
            const refundableAmount = (payment.amount || 0) - alreadyRefunded;
            const canRefund = payment.status === 'success' && refundableAmount > 0.01;

            return (
              <div key={payment.id || i} style={{ marginBottom: i < payments.length - 1 ? 16 : 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>
                    {payment.vendor || '在线支付'}
                    <Tag color={paymentStatus.color} style={{ marginLeft: 8 }}>{paymentStatus.text}</Tag>
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#52c41a' }}>
                    ${Number(payment.amount || 0).toFixed(2)}
                  </div>
                </div>
                <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>
                  交易流水号：{payment.transactionId || '-'}
                </div>
                <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>
                  参考号：{payment.reference || '-'}
                </div>
                {alreadyRefunded > 0 && (
                  <div style={{ fontSize: 12, color: '#ff7a45' }}>
                    已退款：${alreadyRefunded.toFixed(2)}
                  </div>
                )}
                {canRefund && (
                  <Button
                    size="small"
                    color="danger"
                    variant="outline"
                    onClick={() => {
                      refundForm.resetFields();
                      setOtpCooldown(0);
                      setRefundModalVisible(true);
                    }}
                    style={{ marginTop: 8 }}
                  >
                    退款
                  </Button>
                )}
              </div>
            );
          })}
        </Card>
      )}

      {/* 退款记录 */}
      {refunds.length > 0 && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>退款记录</div>
          {refunds.map((refund, i) => (
            <div key={refund.id || i} style={{ marginBottom: i < refunds.length - 1 ? 12 : 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 14, color: '#ff4d4f' }}>-${Number(refund.amount || 0).toFixed(2)}</span>
                <span style={{ fontSize: 12, color: '#999' }}>{refund.createdAt?.slice(0, 16)?.replace('T', ' ')}</span>
              </div>
              {refund.reason && (
                <div style={{ fontSize: 12, color: '#666' }}>原因：{refund.reason}</div>
              )}
            </div>
          ))}
        </Card>
      )}

      {/* 管理员备注 */}
      {order.adminNote && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>备注</div>
          <div style={{ fontSize: 14, color: '#666' }}>{order.adminNote}</div>
        </Card>
      )}

      {/* 发货弹窗 */}
      <Dialog
        visible={shipModalVisible}
        content={
          <Form
            form={shipForm}
            layout="vertical"
            initialValues={{ trackingCarrier: 'SF' }}
            footer={
              <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
                <Button
                  block
                  onClick={() => shipForm.submit()}
                  color="primary"
                >
                  确认发货
                </Button>
                <Button
                  block
                  onClick={() => setShipModalVisible(false)}
                  variant="outline"
                >
                  取消
                </Button>
              </div>
            }
            onFinish={handleShip}
          >
            <Form.Item
              name="trackingNumber"
              label="物流单号"
              rules={[{ required: true, message: '请输入物流单号' }]}
            >
              <Input placeholder="请输入物流单号" />
            </Form.Item>
            <Form.Item
              name="trackingCarrier"
              label="物流公司"
              rules={[{ required: true, message: '请输入物流公司' }]}
            >
              <Input placeholder="顺丰(SF)、圆通(YT)、中通(ZT)等" />
            </Form.Item>
          </Form>
        }
        closeOnMaskClick={false}
      />

      {/* 退款弹窗 */}
      <Dialog
        visible={refundModalVisible}
        content={
          <Form
            form={refundForm}
            layout="vertical"
            footer={
              <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
                <Button
                  block
                  onClick={() => refundForm.submit()}
                  color="danger"
                >
                  确认退款
                </Button>
                <Button
                  block
                  onClick={() => setRefundModalVisible(false)}
                  variant="outline"
                >
                  取消
                </Button>
              </div>
            }
            onFinish={handleRefund}
          >
            <Form.Item
              name="amount"
              label="退款金额"
              rules={[{ required: true, message: '请输入退款金额' }]}
            >
              <Input type="number" placeholder="请输入退款金额" />
            </Form.Item>
            <Form.Item
              name="reason"
              label="退款原因"
            >
              <Input placeholder="请输入退款原因（选填）" />
            </Form.Item>
            <Form.Item
              name="otp"
              label="管理员验证码"
              rules={[{ required: true, message: '请输入 6 位验证码' }]}
              extra={
                <Button
                  size="mini"
                  color="primary"
                  fill="outline"
                  onClick={handleSendRefundOtp}
                  loading={otpSending}
                  disabled={otpCooldown > 0}
                >
                  {otpCooldown > 0 ? `${otpCooldown}s` : '发送验证码'}
                </Button>
              }
            >
              <Input placeholder="6 位数字验证码" maxLength={6} />
            </Form.Item>
          </Form>
        }
        closeOnMaskClick={false}
      />

      {/* 备注弹窗 */}
      <Dialog
        visible={noteModalVisible}
        content={
          <Form
            form={noteForm}
            layout="vertical"
            footer={
              <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
                <Button
                  block
                  onClick={() => noteForm.submit()}
                  color="primary"
                  loading={noteSaving}
                >
                  保存备注
                </Button>
                <Button
                  block
                  onClick={() => setNoteModalVisible(false)}
                  variant="outline"
                >
                  取消
                </Button>
              </div>
            }
            onFinish={handleSaveNote}
          >
            <Form.Item
              name="adminNote"
              label="管理员备注"
            >
              <TextArea placeholder="请输入备注信息" rows={4} />
            </Form.Item>
          </Form>
        }
        closeOnMaskClick={false}
      />

      {/* 编辑收货地址弹窗 */}
      <Dialog
        visible={editAddressModalVisible}
        content={
          <Form
            form={addressForm}
            layout="vertical"
            footer={
              <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
                <Button
                  block
                  onClick={() => addressForm.submit()}
                  color="primary"
                  loading={addressSaving}
                >
                  保存
                </Button>
                <Button
                  block
                  onClick={() => setEditAddressModalVisible(false)}
                  variant="outline"
                >
                  取消
                </Button>
              </div>
            }
            onFinish={handleEditAddressConfirm}
          >
            <Form.Item
              name="shippingFirstName"
              label="收货人名"
              rules={[{ required: true, message: '请填写收货人名' }]}
            >
              <Input placeholder="First Name" />
            </Form.Item>
            <Form.Item
              name="shippingLastName"
              label="收货人姓"
              rules={[{ required: true, message: '请填写收货人姓' }]}
            >
              <Input placeholder="Last Name" />
            </Form.Item>
            <Form.Item
              name="shippingPhone"
              label="联系电话"
              rules={[{ required: true, message: '请填写联系电话' }]}
            >
              <Input placeholder="电话号码" />
            </Form.Item>
            <Form.Item
              name="shippingAddress1"
              label="街道地址1"
              rules={[{ required: true, message: '请填写街道地址' }]}
            >
              <Input placeholder="街道地址1" />
            </Form.Item>
            <Form.Item
              name="shippingAddress2"
              label="街道地址2（可选）"
            >
              <Input placeholder="街道地址2" />
            </Form.Item>
            <Form.Item
              name="shippingCity"
              label="城市"
              rules={[{ required: true, message: '请填写城市' }]}
            >
              <Input placeholder="城市" />
            </Form.Item>
            <Form.Item
              name="shippingProvince"
              label="省/州（可选）"
            >
              <Input placeholder="省/州" />
            </Form.Item>
            <Form.Item
              name="shippingPostalCode"
              label="邮编"
              rules={[{ required: true, message: '请填写邮编' }]}
            >
              <Input placeholder="邮编" />
            </Form.Item>
            <Form.Item
              name="shippingCountry"
              label="国家（可选）"
            >
              <Input placeholder="国家" />
            </Form.Item>
          </Form>
        }
        closeOnMaskClick={false}
      />
    </div>
  );
}

// 订单状态标签
function StatusTag({ status }) {
  const statusMap = {
    pending: { text: '待处理', color: '#faad14' },
    paid: { text: '已支付', color: '#52c41a' },
    shipped: { text: '已发货', color: '#1677ff' },
    completed: { text: '已完成', color: '#722ed1' },
    cancelled: { text: '已取消', color: '#ff4d4f' },
    refunded: { text: '已退款', color: '#ff9845' },
  };

  const config = statusMap[status] || { text: status, color: '#999' };

  return (
    <span
      style={{
        fontSize: 12,
        color: config.color,
        background: `${config.color}15`,
        padding: '2px 8px',
        borderRadius: 4,
      }}
    >
      {config.text}
    </span>
  );
}
